import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { PartForm, SupplierForm, type PartRow, type SupplierRow } from '../components/EntityForms';
import { Modal, Field, TextInput, Select, FormActions } from '../components/form';

type Part = PartRow & { currency: string; suppliers: { name: string } | null; inventory_locations: { name: string } | null };

export function Parts() {
  const { t, i18n } = useTranslation();
  const [parts, setParts] = useState<Part[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [partForm, setPartForm] = useState(false);
  const [editPart, setEditPart] = useState<PartRow | null>(null);
  const [supForm, setSupForm] = useState(false);
  const [editSup, setEditSup] = useState<SupplierRow | null>(null);
  const [movePart, setMovePart] = useState<Part | null>(null);

  const load = useCallback(async () => {
    const [p, s] = await Promise.all([
      supabase.from('parts').select('id,name,sku,unit,unit_cost,currency,on_hand,reorder_level,supplier_id,category_id,location_id,suppliers(name),inventory_locations(name)').is('deleted_at', null).order('name'),
      supabase.from('suppliers').select('id,name,email,phone').is('deleted_at', null).order('name'),
    ]);
    setParts((p.data as unknown as Part[]) ?? []);
    setSuppliers((s.data as SupplierRow[]) ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const lowStock = useMemo(() => parts.filter((p) => p.reorder_level != null && p.on_hand <= p.reorder_level).length, [parts]);

  return (
    <Layout header={<Topbar actions={<><Button variant="secondary" onClick={() => { setEditSup(null); setSupForm(true); }}>+ {t('supplier.new')}</Button><Button onClick={() => { setEditPart(null); setPartForm(true); }}>+ {t('part.new')}</Button></>} />}>
      <div className="flex flex-col gap-[18px] px-7 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.parts')}</h1>
        <section className="grid grid-cols-2 gap-3.5 sm:grid-cols-3">
          <Card className="flex flex-col gap-1 p-[18px]" style={{ background: 'var(--pv-chrome)', color: '#fff' }}><span className="text-xs text-chrome-muted">{t('part.count')}</span><span className="font-mono text-[22px] font-medium">{parts.length}</span></Card>
          <Card className="flex flex-col gap-1 p-[18px]"><span className="text-xs text-muted">{t('part.lowStock')}</span><span className={cn('font-mono text-[22px] font-medium', lowStock && 'text-st-overdue-fg')}>{lowStock}</span></Card>
          <Card className="flex flex-col gap-1 p-[18px]"><span className="text-xs text-muted">{t('nav.suppliers')}</span><span className="font-mono text-[22px] font-medium">{suppliers.length}</span></Card>
        </section>
      </div>

      <div className="flex flex-col gap-[18px] px-7 pb-7 pt-[18px]">
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead><tr className="text-left text-xs text-muted">
                <th className="px-5 py-3 font-medium">{t('part.sku')}</th>
                <th className="px-2 py-3 font-medium">{t('form.name')}</th>
                <th className="px-2 py-3 font-medium">{t('part.onHand')}</th>
                <th className="px-2 py-3 font-medium">{t('inv.location')}</th>
                <th className="px-2 py-3 font-medium">{t('part.unitCost')}</th>
                <th className="px-2 py-3 font-medium">{t('part.supplier')}</th>
                <th className="px-5 py-3" />
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="px-5 py-6 text-muted">{t('common.loading')}</td></tr>
                  : parts.length === 0 ? <tr><td colSpan={7} className="px-5 py-6 text-muted">{t('part.none')}</td></tr>
                  : parts.map((p) => {
                    const low = p.reorder_level != null && p.on_hand <= p.reorder_level;
                    return (
                      <tr key={p.id} className="border-t border-divider hover:bg-bg">
                        <td className="px-5 py-3.5 font-mono text-xs text-muted">{p.sku ?? '—'}</td>
                        <td className="px-2 py-3.5 font-medium">{p.name}</td>
                        <td className="px-2 py-3.5 font-mono">{low ? <StatusPill tone="overdue">{p.on_hand}{p.unit ? ` ${p.unit}` : ''}</StatusPill> : <span>{p.on_hand}{p.unit ? ` ${p.unit}` : ''}</span>}</td>
                        <td className="px-2 py-3.5 text-muted">{p.inventory_locations?.name ?? '—'}</td>
                        <td className="px-2 py-3.5 font-mono text-muted">{formatCurrency(p.unit_cost, p.currency, i18n.language)}</td>
                        <td className="px-2 py-3.5 text-muted">{p.suppliers?.name ?? '—'}</td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setMovePart(p)}>{t('part.move')}</Button>
                            <Button variant="ghost" size="sm" onClick={() => { setEditPart(p); setPartForm(true); }}>{t('form.edit')}</Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Card>

        <h2 className="eyebrow">{t('nav.suppliers')}</h2>
        <Card className="overflow-hidden p-0">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {suppliers.length === 0 && <tr><td className="px-5 py-6 text-muted">{t('supplier.none')}</td></tr>}
              {suppliers.map((s) => (
                <tr key={s.id} className="border-t border-divider first:border-0">
                  <td className="px-5 py-3 font-medium">{s.name}</td>
                  <td className="px-2 py-3 font-mono text-xs text-muted">{s.email ?? '—'}</td>
                  <td className="px-2 py-3 font-mono text-xs text-muted">{s.phone ?? '—'}</td>
                  <td className="px-5 py-3 text-right"><Button variant="ghost" size="sm" onClick={() => { setEditSup(s); setSupForm(true); }}>{t('form.edit')}</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
      <PartForm open={partForm} onClose={() => setPartForm(false)} onSaved={load} initial={editPart} />
      <SupplierForm open={supForm} onClose={() => setSupForm(false)} onSaved={load} initial={editSup} />
      {movePart && <MovementModal part={movePart} onClose={() => setMovePart(null)} onSaved={() => { setMovePart(null); void load(); }} />}
    </Layout>
  );
}

// Record a stock movement (in / out / adjust) via the SECURITY DEFINER RPC.
function MovementModal({ part, onClose, onSaved }: { part: Part; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [type, setType] = useState<'in' | 'out' | 'adjust'>('in');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const n = Number(qty);
    if (!qty.trim() || Number.isNaN(n)) { setErr(t('form.errRequired')); return; }
    setBusy(true);
    const { error } = await supabase.rpc('record_part_movement', { p_part: part.id, p_type: type, p_qty: n, p_reason: reason.trim() || null });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  }
  return (
    <Modal open onClose={onClose} title={`${t('part.move')} — ${part.name}`}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err}</div>}
        <div className="text-sm text-muted">{t('part.onHand')}: <span className="font-mono text-fg">{part.on_hand}{part.unit ? ` ${part.unit}` : ''}</span></div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('part.moveType')}>
            <Select value={type} onChange={(e) => setType(e.target.value as any)}>
              <option value="in">{t('part.moveIn')}</option>
              <option value="out">{t('part.moveOut')}</option>
              <option value="adjust">{t('part.moveAdjust')}</option>
            </Select>
          </Field>
          <Field label={t('part.quantity')} hint={type === 'adjust' ? t('part.adjustHint') : undefined}><TextInput type="number" step="0.001" value={qty} onChange={(e) => setQty(e.target.value)} autoFocus /></Field>
        </div>
        <Field label={t('part.reason')}><TextInput value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}
